import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild, Inject, Output, EventEmitter } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';

import { combineLatest, Observable, Subscription, filter, take, tap } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import moment from 'moment-timezone';
import { Popover } from 'primeng/popover';
import { DomHandler } from 'primeng/dom';

import { EntityType } from '../../../../api/targeting/enum/entity.type.enum';
import { MentionSearchService } from '../../../../Monitor/Core/services/mention-search.service';
import { MentionSearchCriteriaModel } from '../../../../Monitor/Shared/models/mention-search-criteria.model';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { JournodexSourceModel } from '../../../Journodex/models/journodex-source.model';
import { SearchCriteriaSourcesEnum } from '../../../../api/mentions/enum/search-criteria-sources.enum';
import { Mention } from '../../../Mentions/models/mention';
import { SearchStatesEnum } from '../../../../Monitor/Shared/enums/search-states.enum';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { FolderService } from '../../../../Monitor/Core/services/folder.service';
import { FilterService } from '../../../Filter/services/abstract-filter.service';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { Folder } from '../../../../Monitor/Shared/models/folder.model';
import { MentionsService } from '../../../../Monitor/Core/services/mentions.service';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { TagModel } from '../../../Tags/models/tag.model';
import { TagsService } from '../../../Tags/services/tags.service';
import { AppearCondition, AutocompleteCustomOption } from '../../../Forms/components/Autocomplete/autocomplete-custom-option.model';
import { RecentCoverageService } from '../../services/recent-coverage.service';
import { TabComponent } from '../../../Layout/components/Tab/tab.component';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { TimeZoneFormatEnum } from '../../../Misc/enums/time-zone-format.enum';
import { ContactModel } from '../../../TargetingShared/model/contact.model';
import { OutletModel } from '../../../../Discover/Targeting/models/outlet.model';
import { KeywordHighlightModel } from '../../../Misc/models/keyword-highlight.model';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';
import { TargetingSearchesService } from '../../../../Discover/Targeting/services/targeting-searches.service';
import { SearchModeEnum } from '../../../TargetingShared/enum/search-mode.enum';
import { IncludeExcludeModel } from '../../../Misc/models/include-exclude-model';
import { TargetingPreferencesService } from '../../../TargetingShared/services/targeting-preferences.service';
import { PrivateDataModel } from '../../../../Discover/Targeting/models/private-data.model';
import { EntityTypeDefaultImageEnum } from '../../../Misc/enums/entity-type-default-image.enum';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { InfluencerStreamsService } from '../../../../Discover/Targeting/components/InfluencerStreams/influencer-streams-service';
import { ButtonComponent } from '../../../Forms/components/Button/button.component';
import { TargetingSearchService } from '@Discover/Targeting/services/targeting-search.service';
import { TranslateMentionsService } from '../../../Mentions/services/translate-mentions.service';
import { animate, AnimationEvent, keyframes, style, transition, trigger } from '@angular/animations';
import { UserPreferencesService } from '@Account/UserPreferences/services/user-preferences.service';
import { toolbarAnimation } from '@Common/Misc/animations/toolbar-animation';
import { ToolbarStateService } from '@Common/TargetingShared/services/toolbar-state.service';

enum CoverageSaveOptionEnum {
    folder = 'FOLDER',
    tag = 'TAG'
};

@Component({
    selector: 'agpr-recent-coverage-tab',
    templateUrl: './recent-coverage-tab.component.html',
    styleUrls: ['./recent-coverage-tab.component.scss'],
    providers: [
        ToolbarStateService,
        MentionSearchService,
        {
            provide: FilterService,
            useExisting: MentionSearchService
        },
        {
            provide: TabComponent,
            useExisting: RecentCoverageTabComponent
        },
        RecentCoverageService
    ],
    animations: [
        trigger('fade', [
            transition('* => true', [
                animate('300ms ease-in-out', keyframes([
                    style({
                        opacity: 0
                    }),
                    style({
                        opacity: 1
                    })
                ]))
            ]),
            transition('true => *', [
                animate('300ms ease-in-out', keyframes([
                    style({
                        opacity: 1
                    }),
                    style({
                        opacity: 0
                    })
                ]))
            ])
        ]),
        toolbarAnimation
    ],
    standalone: false
})
export class RecentCoverageTabComponent extends TabComponent implements OnInit, OnDestroy {

    @Input() set entity(entity: ContactModel | OutletModel) {
        if (!entity) {

            return;
        }

        this._entity = entity;
        this.entityType = entity.entityType;

        const source = new JournodexSourceModel();
        source.id = entity.id;
        source.entityType = this.entityType === EntityType.OUTLET ? SearchCriteriaSourcesEnum.outlet : SearchCriteriaSourcesEnum.journalist;

        this.criteria = new MentionSearchCriteriaModel();
        this.criteria.query.includedSources = [source];
        this.criteria.isRecentCoverageSearch = true;

        // @TODO : In the case of contact, it might be advisable
        // to also pass as an inclusion the list of his active employers

        this.fetchMentions();
    };
    @Input() contactPrivateData: PrivateDataModel;
    @Input() outletPrivateData: PrivateDataModel;

    @Input() isActive: boolean = true;
    @Input() hideBusinessCard: boolean = false;

    @Output() showRhpTakeOver: EventEmitter<any> = new EventEmitter();

    @ViewChild('tabHeader') declare _header: TemplateRef<any>;
    public get header(): TemplateRef<any> {
        return this._header;
    }
    public set header(value: TemplateRef<any>) {
        this._header = value;
    }
    @ViewChild('tabFooter') declare footer: TemplateRef<any>;
    @ViewChild('mediaPlatformSharingSelector') mediaPlatformSharingSelector: Popover;


    entityType: EntityType;
    _entity: ContactModel | OutletModel;
    imageDefault = EntityTypeDefaultImageEnum;

    userHasMonitoring: boolean = false;
    hideUpsellForReseller: boolean = false;

    openAiPermission: boolean = false;
    userLanguage: string;
    concurrentTranslation: number = 0;

    loading: boolean = true;
    criteria: MentionSearchCriteriaModel;

    mentions: Mention[] = [];
    selectedMentions: Mention[] = [];

    preferences: PreferencesModel;

    tagsList: TagModel[];
    selectedTags: TagModel[] = [];
    tagChips: { label: string; tag: TagModel }[] = [];
    highlightKeywords: KeywordHighlightModel[] = [];

    dropdownFolder: Folder = null;

    folderList$: Observable<Folder[]>;
    tagList$: Observable<TagModel[]>;

    actionBeingPerformed: Subscription;

    coverageSaveOptions: DropdownOptionModel[] = [
        new DropdownOptionModel('Monitoring.source.type.folder', CoverageSaveOptionEnum.folder),
        new DropdownOptionModel('Monitoring.source.type.tag', CoverageSaveOptionEnum.tag),
    ];
    selectedCoverageSaveOption: DropdownOptionModel = this.coverageSaveOptions[0];

    showFilterChips = false;
    pageSize = 10;
    navigateSubscription: Subscription;

    mentionLinksEnabled: boolean;
    previousElementId: string;
    toolbarHeight = 2;

    private subscriptions: Subscription[] = [];

    readonly folderAutocompleteOptions: AutocompleteCustomOption[] = [
        {
            label: 'Monitoring.folderPicker.accountOption',
            action: (searchString: string) => {},
            appearCondition: AppearCondition.always,
            clearOnSelect: true
        },
        {
            label: 'Mention.editDialog.folder.createNewFolder',
            action: (folderName: string) => {
                const folder = new Folder();
                folder.name = folderName;

                this.folderService.create(folder).pipe(
                    switchMap(id => this.folderList$.pipe(
                        map(folders => folders.find(x => x.id === id))
                    ))
                ).subscribe(newFolder => this.dropdownFolder = newFolder);
            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];

    readonly tagsAutocompleteOptions: AutocompleteCustomOption[] = [
        {
            label: 'Tags.createTag',
            action: (searchString: string) => {
                const tagModel = new TagModel();
                tagModel.name = searchString;

                this.tagsService.createTag(tagModel).pipe(
                    switchMap((id) => this.tagsService.getTagById(id))
                ).subscribe(tag => {
                    this.selectedTags.push(tag);
                    this.updateTagsListState();
                });
            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];
    readonly coverageSaveOptionEnum = CoverageSaveOptionEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly entityTypeEnum = EntityType;
    readonly messageSeverities = messageSeverityEnum;
    readonly defaultTimeFormat = TimeZoneFormatEnum.timeZoneFormat;
    readonly mediaTypeEnum = MediaTypeEnum;
    readonly maxConcurrentTranslation: number = 3;

    constructor(
        public mentionSearchService: MentionSearchService,
        private folderService: FolderService,
        private tagsService: TagsService,
        private mentionsService: MentionsService,
        private recentCoverageService: RecentCoverageService,
        private currentUserService: CurrentUserStateService,
        private targetingSearchesService: TargetingSearchesService,
        private targetingPreferencesService: TargetingPreferencesService,
        private router: Router,
        private configurationService: ConfigService,
        public targetingSearchService: TargetingSearchService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private influencerStreamsService: InfluencerStreamsService,
        private translateService: TranslateMentionsService,
        private userPreferencesService: UserPreferencesService,
        public toolbarStateService: ToolbarStateService,
    ){
        super();

        // For reseller accounts, keep the upsell card for Burrelles only
        this.hideUpsellForReseller = this.configurationService.hasBrandGUID() && !this.configurationService.brandGUIDIsBurrelles();
    }

    ngOnInit() {
        this.subscriptions.push(
            this.currentUserService.getPermissions().pipe(filter(x => !!x)).subscribe(permissions => {
                this.openAiPermission = permissions.includes(userPermissionsEnum.openAI);
                this.userHasMonitoring = !!permissions.find(x => x === userPermissionsEnum.advancedMonitoring);
                if (this.userHasMonitoring) {
                    this.pageSize = 25;
                    this.folderList$ = this.folderService.all$;
                    this.tagList$ = this.tagsService.all$;

                    this.subscriptions.push(
                        this.tagList$.subscribe(
                            tags => {
                                this.tagsList = tags;

                                // remove tags that have been deleted
                                this.selectedTags = this.selectedTags.filter((selectedTag) => {
                                    return !!this.tagsList.find(tag => selectedTag.id === tag.id);
                                });

                                this.updateTagsListState();
                            }
                        )
                    );
                }
                this.mentionSearchService.setPageSize(this.pageSize);
            }),
            this.translateService.getLanguage().subscribe(
                language => this.userLanguage = language
            ),
            this.translateService.getConcurrentTranslation().subscribe(
                concurrentTranslation => this.concurrentTranslation = concurrentTranslation
            ),
            this.currentUserService.getPreferences().pipe(filter(x => !!x)).subscribe(
                prefs => this.preferences = prefs
            ),
            this.mentionSearchService.getMentions().pipe(filter(x => !!x)).subscribe(mentions => {
                this.mentions = mentions.map(mention => {
                    mention['fromNowTimestamp'] = moment(mention.datePublished).fromNow();

                    return mention;
                });
                this.selectedMentions = [];
                this.recentCoverageService.applyTwitterInfo(this.mentions);
            }),
            this.mentionSearchService.getFilters().subscribe(filters => {
                this.showFilterChips = !!filters && filters.filter(x => x.chips.length > 0).length > 0;
                this.updateToolbarHeight();
            }),
            this.mentionSearchService.getSearchState().subscribe(state => {
                this.loading = state.type === SearchStatesEnum.loading;
            }),
            this.mentionSearchService.getHighlightKeywords().subscribe(highlightKeywords => {
                this.highlightKeywords = highlightKeywords;
            }),
            this.router.events.subscribe((e) => {
                if (e instanceof NavigationEnd) {
                    this.mentionSearchService.clearFilters();
                }
            }),
            this.targetingSearchesService.currentResource$.pipe(filter(x => !!x)).subscribe(search => {
                if (!search?.monitoringKeywords?.isEmpty() && search.searchMode === SearchModeEnum.contact) {
                    this.mentionSearchService.setSearchedKeywords(search.monitoringKeywords.clone());
                }

                if (!search?.outletMonitoringKeywords?.isEmpty() && search.searchMode === SearchModeEnum.outlet) {
                    this.mentionSearchService.setSearchedKeywords(search.outletMonitoringKeywords.clone());
                }
            }),
            this.targetingPreferencesService.mentionLinksEnabled$.subscribe(enabled => {
                this.mentionLinksEnabled = enabled;
            }),
            combineLatest([
                this.influencerStreamsService.focusedStreamId$,
                this.influencerStreamsService.getStreams()
            ]).subscribe(
                ([focusedStreamId, streams]) => {
                    // Set searched keyword filter if the influencer stream uses key phrase
                    if (focusedStreamId) {
                        const focusedStream = streams.find(stream => stream.id === focusedStreamId);
                        if (focusedStream.keyphrase) {
                            const searchedKeywords = new IncludeExcludeModel<string>();
                            searchedKeywords.include = [focusedStream.keyphrase];
                            this.mentionSearchService.setSearchedKeywords(searchedKeywords);
                        }
                    }
                }
            ),
            this.userPreferencesService.getUserAppPreferences().subscribe(prefs => {
                this.toolbarStateService.toolbarAnimationDisabled = !prefs.generalPrefs.accessibility.collapseToolbars;
            })
        );
    }



    mentionSelected(mention: Mention) {
        if (this.selectedMentions.indexOf(mention) >= 0) {
            mention['selected'] = true;
        }
    }

    toggleSelectAll() {
        if (this.mentions.length === this.selectedMentions.length) {
            this.selectedMentions = [];
        } else {
            this.selectedMentions = this.mentions.slice();
        }
    }

    saveSelectedMentions() {
        if (this.actionBeingPerformed) {
            return;
        }

        const folder: Folder = this.dropdownFolder;
        const addTags = this.selectedTags && this.selectedTags.length > 0;

        let request: Observable<any>;

        if (folder) {
            request = this.folderService.saveMentionsToFolder(this.selectedMentions, folder, !addTags, null, this.selectedTags);
        } else {
            request = this.mentionsService.saveMentionsToAccount(this.selectedMentions, !addTags,  null, this.selectedTags);
        }

        const licensedMentions = this.selectedMentions?.filter(mention => mention.isNla || mention.isCla);
        if (licensedMentions.length > 0) {
            this.unlockMentionClicked(licensedMentions);
        }

        this.setActionBeingPerformed(request);
    }

    clearCoverageSaveOption(): void {
        if (this.selectedCoverageSaveOption.value === this.coverageSaveOptionEnum.folder) {
            this.selectedTags = [];
            this.updateTagsListState();
        } else {
            this.dropdownFolder = null;
        }
    }

    private setActionBeingPerformed(action: Observable<any>): void {
        this.actionBeingPerformed = action.subscribe({
            next: () => {
                if (this.actionBeingPerformed) {
                    this.actionBeingPerformed.unsubscribe();
                    this.actionBeingPerformed = null;
                }
            },
            error: () => {
                if (this.actionBeingPerformed) {
                    this.actionBeingPerformed.unsubscribe();
                }
                this.actionBeingPerformed = null;
            }
        });
    }

    /**
     * Updates the state of the tag selection
     */
    public updateTagsListState(): void {
        if (this.selectedTags) {
            this.tagChips = this.selectedTags.map((tag) => {
                return {
                    label: tag.name,
                    tag: tag
                };
            });
        }
    }

    /**
     * Selects a tag to be used when saving mentions
     */
    public async selectTag(): Promise<void> {
        this.updateTagsListState();
    }

    /**
     * Removes a tag from the list to be used when saving mentions
     * @param index Index of the chip being removed
     */
    public unselectTagChip(index: number): void {
        const removeTag: TagModel = this.tagChips[index].tag;
        this.selectedTags = this.selectedTags.filter((tag) => tag !== removeTag);
        this.updateTagsListState();
    }

    navigateToSubscription(): void {
        if (this.configurationService.brandGUIDIsBurrelles()) {
            window.location.href = 'mailto:inquiry@burrelles.com?subject=Sales%20Inquiry%20-%20Burrelles%20Contact%20Database';
        } else {
            window.open('https://www.agilitypr.com/contact/');
        }
    }

    fetchMentions(): void {
        if (this.criteria) {
            this.mentionSearchService.setSearchCriteria(this.criteria);
            this.mentionSearchService.search(true, false);
        }
    }

    navigateToMonitoring(): void {
        const source = new JournodexSourceModel();
        if (this.entityType === EntityType.OUTLET) {
            source.id = this._entity.id;
            source.entityType = SearchCriteriaSourcesEnum.outlet;
            source.entityName = (this._entity as OutletModel)?.name;
        } else {
            source.id = this._entity.id;
            source.entityType = SearchCriteriaSourcesEnum.journalist;
            source.entityName = (this._entity as ContactModel)?.firstName + ' ' + (this._entity as ContactModel)?.lastName;
        }

        if (this.navigateSubscription) {
            this.navigateSubscription.unsubscribe();
        }

        this.navigateSubscription = this.mentionSearchService.getLatestSearchCriteria().pipe(take(1)).subscribe(criteria => {
            const queryCriteria = criteria;
            queryCriteria.isRecentCoverageSearch = true;
            const oldSourceIndex = queryCriteria.query.includedSources.findIndex(
                includedSource => includedSource.entityName === '' && includedSource.entityType === source.entityType);

            // replace old source with the approriate one
            queryCriteria.query.includedSources.splice(oldSourceIndex, 1, source);

            const queryString = MentionSearchCriteriaModel.toFilterQueryString(queryCriteria);
            window.open(`${this.appBaseHref}Monitoring/search/results?filters=${queryString}`);
        });
    }

    onOutletClick(outledUuid: string) {
        this.showRhpTakeOver.emit(
            {
                outletId: outledUuid,
                contactId: this._entity.entityType === EntityType.CONTACT ? this._entity.id : null,
            }
        );
    }

    showMediaSharing(event, contextButton: ButtonComponent) {
        this.previousElementId = event.target.id;
        this.mediaPlatformSharingSelector.baseZIndex = ++DomHandler.zindex;
        this.mediaPlatformSharingSelector.toggle(event, contextButton.element.nativeElement);
        // set focus to the first media button in overlay panel
        requestAnimationFrame(() => {
            const mediaButton = document.querySelector('[class*="agpr-contentSharing-button"][id*="recentCoverageCard_contentSharing"]');
            if (mediaButton) {
                (mediaButton as HTMLElement).focus();
            }
        });
    }

    hideMediaSharing() {
        this.mediaPlatformSharingSelector.hide();
        const previousButton = document.getElementById(this.previousElementId);
        setTimeout(() => {
            previousButton.focus();
        });
    }

    translate(mention: Mention) {
        if (this.concurrentTranslation < this.maxConcurrentTranslation) {
            this.translateService.setupChatAI(mention, null, true, true);
        }
    }

    revertTranslate(mention: Mention) {
        if (this.concurrentTranslation < this.maxConcurrentTranslation) {
            this.translateService.setDefaultTextAlign(mention);
            mention.showTranslated = false;
            mention.translation = null;
        }
    }

    unlockMentionClicked(mentions: Mention[]) {
        this.influencerStreamsService.refetchMentions(mentions.map(m => m.id), false, true).subscribe(
            response => {
                mentions.forEach(m => {
                    this.mentions.find(visibleMention => visibleMention.id === m.id).url = response[m.id].url;
                });
            }
        );
    }

    /**
     * Delegates scroll updates to the ToolbarStateService, which handles
     * collapsing or expanding the toolbar based on scroll movement.
     *
     * @param scrollTop - The current vertical scroll position of the container.
     */
    handleScroll(scrollTop: number) {
        this.toolbarStateService.onScroll(scrollTop);
    }

    /**
     * Triggered when the toolbar animation starts.
     */
    onAnimationStart(event: AnimationEvent) {
        this.toolbarStateService.onAnimationStart(event);
    }

    /**
     * Triggered when the toolbar animation ends.
     */
    onAnimationDone(event: AnimationEvent) {
        this.toolbarStateService.onAnimationDone(event);
    }
    updateToolbarHeight(): void {
        this.toolbarHeight = this.showFilterChips ? 4.75 : 2;
    }

    ngOnDestroy(): void {
        this.mentionSearchService.clearFilters();
        this.subscriptions.forEach(subscription => subscription.unsubscribe());

        if (this.navigateSubscription) {
            this.navigateSubscription.unsubscribe();
        }
    }
}
